import type { BlissBasicTimeline } from '../config/blissBasic'
import type {
  BlissBasicAccessId,
  BlissBasicFinishId,
  BlissBasicStorageId,
  BlissBasicConfiguratorSelections,
  BlissBasicConfiguratorStepId,
} from '../config/blissBasicConfigurator'
import type { BlissBasicModelId } from '../config/blissBasic'
import type { BlissBasicConfiguratorStepDef } from '../config/blissBasicCatalog'
import { isConfiguratorSelectionStep } from './blissBasicConfiguratorDefaults'

const MODEL_IDS = new Set<string>([
  'twin_full',
  'full_full',
  'triple_stack',
  'quad_corner',
  'not_sure',
])

export function selectedKeysForStep(
  stepId: BlissBasicConfiguratorStepId,
  selections: BlissBasicConfiguratorSelections,
): string[] {
  switch (stepId) {
    case 'layout':
      return selections.modelId ? [selections.modelId] : []
    case 'finish':
      return selections.finishId ? [selections.finishId] : []
    case 'access':
      return selections.accessId ? [selections.accessId] : []
    case 'storage':
      return [...selections.storageIds]
    case 'sleepers':
      return selections.sleeperCount ? [String(selections.sleeperCount)] : []
    case 'timeline':
      return selections.timeline ? [selections.timeline] : []
    default:
      return []
  }
}

export function applyStepSelection(
  stepId: BlissBasicConfiguratorStepId,
  keys: string[],
  patch: (partial: Partial<BlissBasicConfiguratorSelections>) => void,
): void {
  switch (stepId) {
    case 'layout': {
      const key = keys[0]
      const modelId = key && MODEL_IDS.has(key) ? (key as BlissBasicModelId) : 'not_sure'
      patch({ modelId })
      break
    }
    case 'finish':
      patch({ finishId: (keys[0] ?? 'white') as BlissBasicFinishId })
      break
    case 'access':
      patch({ accessId: (keys[0] ?? 'not_sure') as BlissBasicAccessId })
      break
    case 'storage':
      patch({ storageIds: keys as BlissBasicStorageId[] })
      break
    case 'sleepers':
      patch({ sleeperCount: Number(keys[0]) || 4 })
      break
    case 'timeline':
      patch({ timeline: (keys[0] ?? 'exploring') as BlissBasicTimeline })
      break
    default:
      break
  }
}

export function stepRequiresSelection(stepId: BlissBasicConfiguratorStepId): boolean {
  return stepId !== 'storage'
}

export function labelForStepSelection(
  step: BlissBasicConfiguratorStepDef,
  selections: BlissBasicConfiguratorSelections,
): string {
  const keys = selectedKeysForStep(step.id as BlissBasicConfiguratorStepId, selections)
  if (step.id === 'storage' && keys.length === 0) return 'None selected'
  if (keys.length === 0) return '—'
  return keys
    .map((k) => step.items.find((i) => i.choiceKey === k)?.label ?? k)
    .join(', ')
}

export function buildDynamicSummaryNotes(
  steps: BlissBasicConfiguratorStepDef[],
  selections: BlissBasicConfiguratorSelections,
): string {
  const parts = steps
    .filter((s) => isConfiguratorSelectionStep(s.id as BlissBasicConfiguratorStepId))
    .map((s) => `${s.title}: ${labelForStepSelection(s, selections)}`)
  if (selections.notes.trim()) parts.push(`Notes: ${selections.notes.trim()}`)
  return parts.join(' · ')
}
